import { z } from "zod";

/**
 * A logo, and how it sits on whatever it is shown on.
 *
 * Logos come in every shape — a 10:1 wordmark, a square badge, a tall crest — and in
 * every colour, often on a white box of their own. Drawn at one fixed height, a
 * wordmark shrinks to a thread and a crest towers over the heading; drawn as it came,
 * a navy logo vanishes on a navy stage. So the editor measures each logo once, as it
 * is uploaded (its shape, how light or dark it is, what it stands on), and every
 * screen sizes and backs it from those measurements — the projector against the
 * stage's colour, a phone against its own.
 */

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "must be a #rrggbb colour");

/**
 * Small enough to travel inside the deck: a data URL of at most ~40 KB, which the
 * editor produces by trimming and scaling the uploaded image first. SVG is allowed —
 * an image in an <img> runs no script.
 */
export const LOGO_MAX_CHARS = 56_000;
export const logoSrcSchema = z
  .string()
  .max(LOGO_MAX_CHARS)
  .regex(/^data:image\/(png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/, "must be an image data URL");

/** How much of the room it takes, against the same slot. */
export const LOGO_SIZES = ["s", "m", "l"] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];

/**
 * How it stands on the background: `auto` decides from its colours; `plain` is as it
 * came; `light` and `dark` put it on a card of that colour; `white` and `black` draw
 * it in that one colour, for a single-colour logo on a background it would lose to.
 */
export const LOGO_LOOKS = ["auto", "plain", "light", "dark", "white", "black"] as const;
export type LogoLook = (typeof LOGO_LOOKS)[number];

/** Luminance bands a logo's tones are counted in, darkest first. */
export const LOGO_BANDS = 8;

export const logoSchema = z
  .object({
    src: logoSrcSchema,
    /** Width over height, once its empty margins are trimmed. Null for a logo saved before it was measured. */
    ratio: z.number().min(0.05).max(40).nullable().default(null),
    /**
     * The share of its visible pixels in each luminance band, darkest first — enough
     * to tell, against any background, how much of it would be lost there.
     */
    tones: z.array(z.number().min(0).max(1)).length(LOGO_BANDS).nullable().default(null),
    /** The solid colour it stands on, when it came on a box of its own (a JPEG on white); null when transparent. */
    fill: hex.nullable().default(null),
    size: z.enum(LOGO_SIZES).default("m"),
    look: z.enum(LOGO_LOOKS).default("auto"),
  })
  .strict();

export type Logo = z.infer<typeof logoSchema>;

/** A logo as a theme holds it — and, from before logos were measured, a bare data URL. */
export const themeLogoSchema = z.preprocess((v) => (typeof v === "string" ? { src: v } : v), logoSchema.nullable());

/** The text colour a light stage uses (themeColours), and so the dark card's. */
const INK = "#17142a";
const PAPER = "#ffffff";

function relativeLuminance(hexColour: string): number {
  const n = parseInt(hexColour.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

const ratioOf = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/** Which band a luminance falls in: even steps of lightness, as the eye sees it, not of light. */
export function logoBand(lum: number): number {
  return Math.min(LOGO_BANDS - 1, Math.floor(Math.max(0, lum) ** (1 / 2.2) * LOGO_BANDS));
}

/** A band's middle, as a luminance. */
const bandLuminance = (band: number) => ((band + 0.5) / LOGO_BANDS) ** 2.2;

/** Below this contrast with its background, a part of a logo can't be made out from the back of a room. */
const VISIBLE = 1.8;

/** The share of a logo that would be lost on this background. */
export function logoLost(tones: number[], background: string): number {
  const bg = relativeLuminance(background);
  return tones.reduce((sum, share, band) => sum + (ratioOf(bandLuminance(band), bg) < VISIBLE ? share : 0), 0);
}

export type LogoPaint = {
  /** The card behind it, if it needs one. */
  plate: string | null;
  /** Drawn in one colour, if so. */
  tint: "white" | "black" | null;
};

/**
 * How to draw a logo on a background. `auto` leaves a logo alone where it shows up,
 * keeps one that came on a box of its own on a card of that box's colour (so the box
 * reads as a badge, not a mistake), and otherwise backs it with whichever card loses
 * the least of it.
 */
export function logoPaint(logo: Pick<Logo, "tones" | "fill" | "look">, background: string): LogoPaint {
  switch (logo.look) {
    case "plain":
      return { plate: null, tint: null };
    case "light":
      return { plate: PAPER, tint: null };
    case "dark":
      return { plate: INK, tint: null };
    case "white":
    case "black":
      // A logo on a solid box would become a solid block.
      return logo.fill ? { plate: null, tint: null } : { plate: null, tint: logo.look };
    case "auto": {
      if (logo.fill) {
        // A box the colour of the background already blends in.
        return { plate: ratioOf(relativeLuminance(logo.fill), relativeLuminance(background)) < 1.15 ? null : logo.fill, tint: null };
      }
      if (!logo.tones || logoLost(logo.tones, background) < 0.25) return { plate: null, tint: null };
      return { plate: logoLost(logo.tones, PAPER) <= logoLost(logo.tones, INK) ? PAPER : INK, tint: null };
    }
  }
}

const SCALE: Record<LogoSize, number> = { s: 0.78, m: 1, l: 1.28 };

export type LogoBox = {
  /** Null for a logo of unknown shape (saved before logos were measured): as wide as it comes, up to `maxWidth`. */
  width: number | null;
  height: number;
  maxWidth: number;
  /** Around it, when it stands on a card. */
  pad: number;
};

/**
 * A logo's size, in multiples of its slot's height, so that logos of every shape carry
 * the same weight: each gets the area a 3:1 wordmark would have at the slot's height,
 * then is kept from growing too tall or too wide. A square badge comes out a little
 * taller than the slot, a long wordmark lower and much wider.
 */
export function logoBox(logo: Pick<Logo, "ratio" | "size">): LogoBox {
  const s = SCALE[logo.size];
  const [maxHeight, maxWidth, pad] = [1.7 * s, 5.5 * s, 0.26 * s];
  if (!logo.ratio) return { width: null, height: s, maxWidth, pad };
  let height = Math.sqrt((3 * s * s) / logo.ratio);
  let width = height * logo.ratio;
  if (height > maxHeight) [height, width] = [maxHeight, maxHeight * logo.ratio];
  if (width > maxWidth) [height, width] = [maxWidth / logo.ratio, maxWidth];
  return { width, height, maxWidth, pad };
}
